import {
  QUESTION_LIST_COLUMNS_WITHOUT_BLANK_FILTER,
  withoutBlankFilterChoices,
} from "@/components/non_registrant/dashboard-column-filter-match";
import type { ColumnProperties } from "@/components/non_registrant/dashboard-table";
import { DashboardTable } from "@/components/non_registrant/dashboard-table";
import { Button } from "@/components/ui/button";
import ErrorComponent from "@/components/ui/error";
import LoadingScreen from "@/components/ui/loading-screen";
import { UserType } from "@/constants";
import { getAllLicenseTypeQuestionsOptions } from "@/hera-api/client/@tanstack/react-query.gen";
import type {
  NonRegistrantQuestionResponseSchema,
  QuestionScope,
} from "@/hera-api/client/types.gen";
import { useFilteredDashboardQuestions } from "@/hooks/use-filtered-dashboard-questions";
import { useGovernmentPortalJurisdictionId } from "@/hooks/use-government-portal-jurisdiction-id";
import { useListColumnFilterSync } from "@/hooks/use-sync-global-column-filters";
import { getAnswerOptionKeysInConfiguredOrder } from "@/lib/answer-option-order";
import { getCreateFormFilterDefaults } from "@/lib/create-form-filter-defaults";
import { questionJurisdictionMatchesColumnFilter } from "@/lib/filter-dashboard-questions";
import { isGovernmentPortalViewer } from "@/lib/government-impersonate";
import type { NamedIdOption } from "@/lib/sync-global-column-filters";
import {
  assertUnreachable,
  compareDateStrings,
  formatDateTime,
  formatQuestionColumnFullValue,
  getQueryErrorHeading,
  getQueryErrorMessage,
  getUserType,
} from "@/lib/utils";
import { useAuth0 } from "@auth0/auth0-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Plus, Upload as UploadIcon } from "lucide-react";
import { useCallback, useState } from "react";
import { JurisdictionFilterWarningDialog } from "../ui/jurisdiction-filter-warning-dialog";
import { QuestionDetailsSheet } from "./question-details-sheet";
import { QuestionFlowDesignerEntry } from "./question-flow-designer";
import { QuestionUploadSheet } from "./question-upload-sheet";

// Define your accessor keys as a const array
const QUESTION_ACCESSOR_KEYS = [
  "id",
  "scope",
  "license_type_name",
  "jurisdiction_name",
  "question_group_name",
  "display_order",
  "jump_target",
  "question",
  "answer_type",
  "answer_options",
  "next_options",
  "information_text",
  "answer_required",
  "display_up_front",
  "show_on_fees_payment_screen",
  "select_from_fixed_list",
  "url_template",
  "copy_value_to_clipboard",
  "allow_multiple_photos",
  "allow_multiple_urls",
  "last_updated_at",
  "active",
] as const;

type QuestionAccessorKeys = (typeof QUESTION_ACCESSOR_KEYS)[number];
type QuestionColumnProperties = ColumnProperties<
  NonRegistrantQuestionResponseSchema,
  QuestionAccessorKeys
>;

const buildQuestionColumnProperties = (
  accessorKey: QuestionAccessorKeys,
): QuestionColumnProperties => {
  switch (accessorKey) {
    case "id":
      return {
        displayName: "ID",
        defaultVisible: false,
        getValue: (row) => row.id,
      };
    case "scope":
      return {
        displayName: "Scope",
        defaultVisible: true,
        getValue: (row) => row.scope,
      };
    case "license_type_name":
      return {
        displayName: "License Type",
        defaultVisible: true,
        getValue: (row) => row.license_type_name,
      };
    case "jurisdiction_name":
      return {
        displayName: "Jurisdiction",
        defaultVisible: true,
        getValue: (row) => row.jurisdiction_name || "",
        rowMatchesFilter: (row, filterValues) =>
          questionJurisdictionMatchesColumnFilter(row, filterValues),
      };
    case "question_group_name":
      return {
        displayName: "Group",
        defaultVisible: true,
        getValue: (row) => row.question_group_name ?? "",
      };
    case "active":
      return {
        displayName: "Active",
        defaultVisible: true,
        getValue: (row) => (!row.is_deleted ? "Yes" : "No"),
      };
    case "display_order":
      return {
        displayName: "Order",
        defaultVisible: true,
        getValue: (row) => row.display_order.toString(),
        sortingFn: (rowA, rowB) =>
          rowA.original.display_order - rowB.original.display_order,
      };
    case "jump_target":
      return {
        displayName: "Next Question",
        defaultVisible: true,
        getValue: (row) => {
          const v = row.jump_target;
          if (v == null) return "";
          if (v === -1) return "End";
          return v.toString();
        },
      };
    case "question":
      return {
        displayName: "Question",
        defaultVisible: true,
        getValue: (row) => formatQuestionColumnFullValue(row.question),
      };
    case "answer_type":
      return {
        displayName: "Answer Type",
        defaultVisible: true,
        getValue: (row) => {
          // Convert snake_case to Title Case for display
          return row.answer_type
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
        },
      };
    case "answer_options":
      return {
        displayName: "Answer Options",
        defaultVisible: true,
        getValue: (row) => {
          if (
            !row.answer_options ||
            Object.keys(row.answer_options).length === 0
          ) {
            return "";
          }
          return getAnswerOptionKeysInConfiguredOrder(
            row.answer_options,
            row.answer_option_keys_order,
          ).join(", ");
        },
      };
    case "next_options":
      return {
        displayName: "Next Options",
        defaultVisible: true,
        getValue: (row) => {
          if (
            !row.answer_options ||
            Object.keys(row.answer_options).length === 0
          ) {
            return "";
          }
          const keys = getAnswerOptionKeysInConfiguredOrder(
            row.answer_options,
            row.answer_option_keys_order,
          );
          return keys
            .map((k) => row.answer_options?.[k])
            .map((v) => {
              if (v == null) return "-";
              if (v === -1) return "End";
              return v.toString();
            })
            .join(", ");
        },
      };
    case "information_text":
      return {
        displayName: "Information Text",
        defaultVisible: false,
        getValue: (row) => row.information_text || "",
      };
    case "answer_required":
      return {
        displayName: "Required",
        defaultVisible: true,
        getValue: (row) => (row.answer_required ? "Yes" : "No"),
      };
    case "display_up_front":
      return {
        displayName: "Display Up Front",
        defaultVisible: true,
        getValue: (row) => (row.display_up_front ? "Yes" : "No"),
      };
    case "show_on_fees_payment_screen":
      return {
        displayName: "Fees Payment Screen",
        defaultVisible: true,
        getValue: (row) => (row.show_on_fees_payment_screen ? "Yes" : "No"),
      };
    case "select_from_fixed_list":
      return {
        displayName: "Select From Fixed List",
        defaultVisible: true,
        getValue: (row) => (row.select_from_fixed_list ? "Yes" : "No"),
      };
    case "url_template":
      return {
        displayName: "URL Template",
        defaultVisible: false,
        getValue: (row) => row.url_template || "",
      };
    case "copy_value_to_clipboard":
      return {
        displayName: "Copy to Clipboard",
        defaultVisible: false,
        getValue: (row) => (row.copy_value_to_clipboard ? "Yes" : "No"),
      };
    case "allow_multiple_photos":
      return {
        displayName: "Multiple Photos",
        defaultVisible: false,
        getValue: (row) => (row.allow_multiple_photos ? "Yes" : "No"),
      };
    case "allow_multiple_urls":
      return {
        displayName: "Multiple URLs",
        defaultVisible: false,
        getValue: (row) => (row.allow_multiple_urls ? "Yes" : "No"),
      };
    case "last_updated_at":
      return {
        displayName: "Updated At",
        defaultVisible: true,
        getValue: (row) =>
          row.last_updated_at ? formatDateTime(row.last_updated_at) : "",
        sortingFn: (rowA, rowB) =>
          compareDateStrings(
            rowA.original.last_updated_at,
            rowB.original.last_updated_at,
          ),
      };

    default:
      assertUnreachable(accessorKey);
  }
};

const getQuestionColumnProperties = (
  accessorKey: QuestionAccessorKeys,
): QuestionColumnProperties =>
  withoutBlankFilterChoices(
    accessorKey,
    QUESTION_LIST_COLUMNS_WITHOUT_BLANK_FILTER,
    buildQuestionColumnProperties(accessorKey),
  );

interface DashboardQuestionsProps {
  isAuthSetupComplete: boolean;
  selectedJurisdictions: string[];
  onSelectedJurisdictionsChange?: (ids: string[]) => void;
  jurisdictionCatalog?: readonly NamedIdOption[];
  selectedLicenseTypes?: string[];
  onSelectedLicenseTypesChange?: (ids: string[]) => void;
  licenseTypeCatalog?: readonly NamedIdOption[];
}

export function DashboardQuestions({
  isAuthSetupComplete,
  selectedJurisdictions,
  onSelectedJurisdictionsChange,
  jurisdictionCatalog = [],
  selectedLicenseTypes = [],
  onSelectedLicenseTypesChange,
  licenseTypeCatalog = [],
}: DashboardQuestionsProps) {
  // Helper to convert backend scope to simplified scope
  const getSimplifiedScope = useCallback(
    (backendScope: QuestionScope): "global" | "group" | "jurisdiction" => {
      const scope = backendScope as string;
      if (scope.startsWith("global") || scope === "global") return "global";
      if (scope.startsWith("group")) return "group";
      if (scope.startsWith("jurisdiction") || scope === "jurisdiction")
        return "jurisdiction";
      return "global"; // fallback
    },
    [],
  );

  const [selectedQuestionId, setSelectedQuestionId] = useState<string | null>(
    null,
  );
  const [selectedQuestionScope, setSelectedQuestionScope] = useState<
    "global" | "group" | "jurisdiction" | null
  >(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isWarningDialogOpen, setIsWarningDialogOpen] = useState(false);
  const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>({
    active: ["Yes"],
  });
  const [duplicateQuestionData, setDuplicateQuestionData] = useState<
    any | null
  >(null);
  const [isUploadSheetOpen, setIsUploadSheetOpen] = useState(false);
  const queryClient = useQueryClient();

  // Questions query to get data for the dashboard
  const {
    data: questionsData,
    isLoading: isQuestionsQueryLoading,
    error: questionsQueryError,
  } = useQuery({
    ...getAllLicenseTypeQuestionsOptions({
      query: {
        include_deleted: true,
      },
    }),
    enabled: isAuthSetupComplete,
  });

  const {
    availableQuestions,
    jurisdictions,
    isJurisdictionsLoading,
    jurisdictionsQueryError,
  } = useFilteredDashboardQuestions({
    questions: questionsData?.questions,
    questionType: "registration",
    selectedJurisdictions,
    selectedLicenseTypes,
    isAuthSetupComplete,
  });

  // Handle row click
  const handleRowClick = useCallback(
    (questionId: string) => {
      setSelectedQuestionId(questionId);
      const questionScope = questionsData?.questions?.find(
        (q) => q.id === questionId,
      )?.scope;
      setSelectedQuestionScope(
        questionScope ? getSimplifiedScope(questionScope) : null,
      );
      setIsSheetOpen(true);
    },
    [questionsData?.questions, getSimplifiedScope],
  );

  const { user } = useAuth0();
  const userType = getUserType(user);
  const isGovernment = isGovernmentPortalViewer(userType);
  const isOperator = userType === UserType.OPERATOR && !isGovernment;
  const { jurisdictionId: governmentJurisdictionId } =
    useGovernmentPortalJurisdictionId(isGovernment);

  const openNewQuestionSheet = useCallback(() => {
    setSelectedQuestionId(null);
    const defaults = getCreateFormFilterDefaults({
      selectedJurisdictions,
      selectedLicenseTypes,
      fallbackScope: "global",
      forceJurisdictionScope: isGovernment,
      governmentJurisdictionId,
    });
    setSelectedQuestionScope(defaults.scope);
    setIsSheetOpen(true);
  }, [
    isGovernment,
    governmentJurisdictionId,
    selectedJurisdictions,
    selectedLicenseTypes,
  ]);

  // Handle add new question (jurisdiction filter warning only in operator portal)
  const handleAddNewQuestion = useCallback(() => {
    if (isOperator && selectedJurisdictions.length > 0) {
      setIsWarningDialogOpen(true);
    } else {
      openNewQuestionSheet();
    }
  }, [isOperator, selectedJurisdictions.length, openNewQuestionSheet]);

  // Handle duplicate
  const handleDuplicate = useCallback(
    (questionId: string) => {
      const question = questionsData?.questions?.find(
        (q) => q.id === questionId,
      );
      if (question) {
        // Remove id and timestamps, keep all other fields
        const { id, last_updated_at, ...rest } = question;
        // For government users, always duplicate as jurisdiction-specific
        // For other users, if jurisdiction_id is present, set scope to 'jurisdiction'
        const duplicateData = {
          ...rest,
          scope: isGovernment
            ? "jurisdiction"
            : rest.jurisdiction_id
              ? "jurisdiction"
              : rest.scope,
          jurisdiction_id: isGovernment
            ? governmentJurisdictionId
            : rest.jurisdiction_id,
        };
        setDuplicateQuestionData(duplicateData);
        setSelectedQuestionId(null); // Ensure it's treated as new
        setIsSheetOpen(true);
      }
    },
    [questionsData?.questions, isGovernment, governmentJurisdictionId],
  );

  useListColumnFilterSync({
    columnFilters,
    setColumnFilters,
    jurisdictionColumnId: "jurisdiction_name",
    licenseTypeColumnId: "license_type_name",
    selectedJurisdictions,
    setSelectedJurisdictions: onSelectedJurisdictionsChange,
    jurisdictions: jurisdictionCatalog,
    selectedLicenseTypes,
    setSelectedLicenseTypes: onSelectedLicenseTypesChange,
    licenseTypes: licenseTypeCatalog,
  });

  if (isQuestionsQueryLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <LoadingScreen message="Fetching questions..." />
      </div>
    );
  }
  if (questionsQueryError) {
    return (
      <ErrorComponent
        title={getQueryErrorHeading(questionsQueryError)}
        message={getQueryErrorMessage(questionsQueryError)}
      />
    );
  }

  if (jurisdictionsQueryError) {
    return (
      <ErrorComponent
        title={getQueryErrorHeading(jurisdictionsQueryError)}
        message={getQueryErrorMessage(jurisdictionsQueryError)}
      />
    );
  }

  if (isJurisdictionsLoading) {
    return <LoadingScreen message="Loading jurisdiction question groups..." />;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 min-w-0">
      <div className="flex shrink-0 justify-between items-center">
        <div />
        <div className="flex items-center gap-2">
          <QuestionFlowDesignerEntry
            questionType="registration"
            allQuestions={questionsData?.questions ?? []}
            jurisdictions={jurisdictions}
            selectedJurisdictionIds={selectedJurisdictions}
            selectedLicenseTypeIds={selectedLicenseTypes}
            title="Registration question flow"
            readOnly={isGovernment}
            onSaved={() => {
              void queryClient.invalidateQueries({
                queryKey: getAllLicenseTypeQuestionsOptions({
                  query: { include_deleted: true },
                }).queryKey,
              });
            }}
          />
          {isOperator && (
            <Button onClick={() => setIsUploadSheetOpen(true)} type="button">
              <UploadIcon className="h-4 w-4 mr-2" />
              Upload Questions
            </Button>
          )}
          <Button onClick={handleAddNewQuestion}>
            <Plus className="h-4 w-4 mr-2" />
            Add Question
          </Button>
        </div>
      </div>

      <DashboardTable<NonRegistrantQuestionResponseSchema, QuestionAccessorKeys>
        data={availableQuestions}
        onRowClick={handleRowClick}
        accessorKeys={QUESTION_ACCESSOR_KEYS}
        getColumnProperties={getQuestionColumnProperties}
        getRowId={(row) => row.id}
        tablePersistenceKey="non-registrant-registration-questions"
        initialSortColumn="last_updated_at"
        initialSortDirection="desc"
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        renderRowActions={(row) => (
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              handleDuplicate(row.id);
            }}
            className="ml-1"
            aria-label="Duplicate Question"
            title="Duplicate Question"
          >
            <Copy className="h-4 w-4" />
          </Button>
        )}
      />

      <QuestionDetailsSheet
        isOpen={isSheetOpen}
        onOpenChange={(open) => {
          setIsSheetOpen(open);
          if (!open) setDuplicateQuestionData(null);
        }}
        questionId={selectedQuestionId}
        scope={selectedQuestionScope}
        duplicateQuestionData={duplicateQuestionData}
        filterJurisdictionIds={selectedJurisdictions}
        filterLicenseTypeIds={selectedLicenseTypes}
        key={
          selectedQuestionId || (duplicateQuestionData ? "duplicate" : "new")
        }
      />

      {isOperator && (
        <JurisdictionFilterWarningDialog
          isOpen={isWarningDialogOpen}
          onOpenChange={setIsWarningDialogOpen}
          onConfirm={() => {
            setIsWarningDialogOpen(false);
            openNewQuestionSheet();
          }}
          selectedJurisdictionsCount={selectedJurisdictions.length}
          recordType="question"
        />
      )}

      {isOperator && (
        <QuestionUploadSheet
          isOpen={isUploadSheetOpen}
          onOpenChange={setIsUploadSheetOpen}
          questionType="registration"
        />
      )}
    </div>
  );
}
